import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlfredClientConfig } from '../../alfred-client.config';
import { SelfExamAnswerResponse } from '../../models/self-exam-answer-response.model';
import { SessionList } from '../../models/session-list.model';
import { Session } from '../../models/session.model';
import { Step } from '../../models/step/step.model';
import { StepMapper } from '../../step-mapper';
import {
  PatchSelfExamAnswerAsCorrectRequest,
  PostSessionStepRequest,
} from './session.request';
import {
  GetSessionStepResponse,
  PatchSelfExamAnswerAsCorrectResponse,
  SessionResponse,
} from './session.response';

@Injectable({
  providedIn: 'root',
})
export class SessionApi {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);

  /**
   * Get the entire list of available sessions for the user
   */
  public getSessions(userId: string): Observable<SessionList> {
    const url: string = `${this.config.alfredApiUrl}/api/users/${userId}/sessions`;
    const headers: HttpHeaders = new HttpHeaders({
      accept: 'application/json',
    });

    return this.httpClient
      .get<SessionResponse[]>(url, { headers: headers })
      .pipe(
        map((response: any[]) => response.map((item) => Session.fromDto(item))),
        map((sessions) => new SessionList(sessions)),
      );
  }

  /**
   * Get the first step of a questionnaire or if the user already started
   * the questionnaire in the past, return the question where the user left off
   */
  public initialStep(sessionId: string): Observable<Step> {
    const url: string = `${this.config.alfredApiUrl}/api/sessions/${sessionId}/step`;
    const headers: HttpHeaders = new HttpHeaders({
      accept: 'application/json',
    });

    return this.httpClient
      .get<GetSessionStepResponse>(url, { headers: headers })
      .pipe(
        map((response) => this.removeFeedback(response)),
        map((response) => StepMapper.fromDto(response)),
      );
  }

  /**
   * Get the next step of a questionnaire.
   */
  public nextStep(
    sessionId: string,
    request: PostSessionStepRequest,
  ): Observable<Step> {
    const url: string = `${this.config.alfredApiUrl}/api/sessions/${sessionId}/step/next`;
    const headers: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');

    return this.httpClient
      .patch<GetSessionStepResponse>(url, request, { headers: headers })
      .pipe(
        map((response) => this.removeFeedback(response)),
        map((response) => StepMapper.fromDto(response)),
      );
  }

  /**
   * Get the prev step of a questionnaire.
   */
  public previousStep(
    sessionId: string,
    request: PostSessionStepRequest,
  ): Observable<Step> {
    const url: string = `${this.config.alfredApiUrl}/api/sessions/${sessionId}/step/previous`;
    const headers: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');

    return this.httpClient
      .patch<GetSessionStepResponse>(url, request, { headers: headers })
      .pipe(
        map((response) => this.removeFeedback(response)),
        map((response) => StepMapper.fromDto(response)),
      );
  }

  /**
   * Make a PATCH call to the API to save whether or not the user thinks the
   * given answer matches the correct answer.
   */
  public patchSelfExamAnswerAsCorrect(
    requestData: PatchSelfExamAnswerAsCorrectRequest,
  ): Observable<SelfExamAnswerResponse> {
    const url: string = `${this.config.alfredApiUrl}/api/self-exam-answers/${requestData.answerId}`;
    const headers: HttpHeaders = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/merge-patch+json');

    return this.httpClient
      .patch<PatchSelfExamAnswerAsCorrectResponse>(
        url,
        { submittedAnswerAsCorrect: requestData.submittedAnswerAsCorrect },
        { headers: headers },
      )
      .pipe(map((response) => SelfExamAnswerResponse.fromDto(response)));
  }

  /**
   * Method to denormalize the given response into the correct Class model.
   */
  private denormalizeStep(response: GetSessionStepResponse): Step {
    return StepMapper.fromDto(response);
  }

  /**
   * Because of compatibility issues with version 2.0 empty feedback response are
   * send with. { ignore: true }
   */
  private removeFeedback(
    response: GetSessionStepResponse,
  ): GetSessionStepResponse {
    if (response?.feedback?.ignore) {
      return {
        ...response,
        feedback: undefined,
      };
    }

    return response;
  }
}
